/**
 * ApplicationsKanban — recruiter pipeline view across all of their job posts.
 * Drag-and-drop is intentionally optional; primary interaction is a status dropdown
 * on each candidate card. Columns: Applied · Viewed · Shortlisted · Interview · Selected · Rejected · On Hold.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/Skeleton";
import {
  Users, ArrowLeft, MoreVertical, Briefcase, ArrowRight, Inbox, ChevronDown, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const STAGES = [
  { key: "applied",     label: "Applied",     chip: "tb-chip-lavender" },
  { key: "viewed",      label: "Viewed",      chip: "tb-chip-sky" },
  { key: "shortlisted", label: "Shortlisted", chip: "tb-chip-butter" },
  { key: "interview",   label: "Interview",   chip: "tb-chip-mint" },
  { key: "selected",    label: "Selected",    chip: "tb-chip-mint" },
  { key: "rejected",    label: "Rejected",    chip: "tb-chip-rose" },
  { key: "on_hold",     label: "On hold",     chip: "tb-chip-sky" },
];

export default function ApplicationsKanban() {
  const navigate = useNavigate();
  const [apps, setApps] = useState(null);
  const [moving, setMoving] = useState(null);

  useEffect(() => {
    api.get("/marketplace/applications/received").then((r) => setApps(r.data || [])).catch(() => setApps([]));
  }, []);

  const grouped = useMemo(() => {
    const out = Object.fromEntries(STAGES.map((s) => [s.key, []]));
    (apps || []).forEach((a) => {
      const k = STAGES.find((s) => s.key === a.status) ? a.status : "applied";
      out[k].push(a);
    });
    return out;
  }, [apps]);

  const move = async (app, newStatus) => {
    setMoving(app.app_id);
    try {
      await api.post(`/marketplace/applications/${app.app_id}/status`, { status: newStatus });
      setApps((curr) => curr.map((a) => a.app_id === app.app_id ? { ...a, status: newStatus } : a));
      toast.success(`Moved to ${STAGES.find((s) => s.key === newStatus)?.label}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not move");
    } finally {
      setMoving(null);
    }
  };

  return (
    <Layout title="Applications Pipeline">
      <Button variant="ghost" size="sm" onClick={() => navigate("/hiring")} className="rounded-full mb-3" data-testid="apps-back">
        <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back to hiring hub
      </Button>

      <div className="mb-5">
        <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <Users className="h-3 w-3" /> Hiring · Pipeline
        </span>
        <h1 className="mt-3 font-display text-3xl tracking-tight font-medium leading-tight">Application pipeline</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">Every candidate across all your job posts. Move them through stages with one click.</p>
      </div>

      {!apps ? (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3">
          {STAGES.map((s) => <Skeleton key={s.key} className="h-64" />)}
        </div>
      ) : apps.length === 0 ? (
        <div className="tb-card p-10 text-center" data-testid="apps-empty">
          <span className="mx-auto h-14 w-14 grid place-items-center rounded-full border border-dashed border-border mb-3">
            <Inbox className="h-6 w-6 text-muted-foreground" />
          </span>
          <p className="font-display text-lg font-medium">No applications yet</p>
          <p className="mt-1 text-sm text-muted-foreground">When teachers apply to your jobs, they&apos;ll appear here.</p>
          <Button className="rounded-full mt-4" onClick={() => navigate("/hiring/jobs/new")}>Post a job <ArrowRight className="h-3.5 w-3.5 ml-1.5" /></Button>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-3 px-3 tb-no-scrollbar">
          <div className="grid grid-flow-col auto-cols-[260px] gap-3" data-testid="apps-board">
            {STAGES.map((stage) => (
              <Column key={stage.key} stage={stage} apps={grouped[stage.key]} onMove={move} moving={moving} />
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}

function Column({ stage, apps, onMove, moving }) {
  return (
    <div className="rounded-2xl border border-border bg-card/40 p-3 min-h-[300px]" data-testid={`apps-col-${stage.key}`}>
      <div className="flex items-center justify-between mb-3">
        <span className={`${stage.chip} rounded-full text-[10px] px-2 py-0.5 font-bold uppercase tracking-wide`}>{stage.label}</span>
        <span className="text-xs text-muted-foreground font-mono">{apps.length}</span>
      </div>
      <div className="space-y-2">
        {apps.map((a) => <Card key={a.app_id} app={a} onMove={onMove} moving={moving} />)}
        {apps.length === 0 && (
          <p className="text-[11px] text-muted-foreground italic py-3 text-center">No candidates</p>
        )}
      </div>
    </div>
  );
}

function Card({ app, onMove, moving }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3 hover:border-primary/40 transition" data-testid={`apps-card-${app.app_id}`}>
      <div className="flex items-start gap-2 mb-1.5">
        <span className="h-7 w-7 rounded-full tb-chip-lavender grid place-items-center font-display text-xs font-medium shrink-0">
          {(app.applicant_name || "?").slice(0, 1)}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{app.applicant_name || "Candidate"}</p>
          <p className="text-[11px] text-muted-foreground truncate inline-flex items-center gap-1">
            <Briefcase className="h-2.5 w-2.5" /> {app.job?.role_title || "—"}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" data-testid={`apps-card-menu-${app.app_id}`}>
              {moving === app.app_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <MoreVertical className="h-3 w-3" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em]">Move to</DropdownMenuLabel>
            {STAGES.filter((s) => s.key !== app.status).map((s) => (
              <DropdownMenuItem key={s.key} onClick={() => onMove(app, s.key)} data-testid={`apps-move-${app.app_id}-${s.key}`}>
                <ChevronDown className="h-3 w-3 mr-2" /> {s.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {app.cover_letter && (
        <p className="text-[11px] text-muted-foreground line-clamp-2 italic mt-1">&ldquo;{app.cover_letter.slice(0, 100)}&rdquo;</p>
      )}
      <p className="text-[10px] text-muted-foreground mt-2">{new Date(app.created_at).toLocaleDateString()}</p>
    </div>
  );
}
